/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import {
  Ad,
  AdBreak,
  AdBreakInfo,
  AdContentTimePosition,
  StreamTimelineMetadata,
  AdDeliveryProperties,
  AdInfo,
  VASTProperties,
} from '@quickplay/player-interface';
import { createError, Error, ErrorCodes } from '@quickplay/foundation';
import { AdsRenderingSettings } from '../../models/AdsRenderingSettings';
import { ImaSdkSettings } from '../../models/ImaSdkSettings';
import { IMAAdRequest } from '../../models/csai/IMAAdRequest';
import { SsaiAdRequest } from '../../models/ssai/SsaiAdRequest';
import { LiveSsaiAdsRequest } from '../../models/ssai/live/LiveSsaiAdsRequest';
import { VodSsaiAdsRequest } from '../../models/ssai/vod/VodSsaiAdsRequest';
import { ImaSsaiErrorCodes } from '../ssai/ImaSsaiErrorCodes';
import { v4 as uuid } from 'uuid';

/** Dash-free UUID identifying one ad's session. */
const createAdSessionId = (): string => uuid().replace(/-/g, '');

/**
 * Playhead positions are given in seconds and can contain decimals, for example, 37.584s.
 * The constant TIME_THRESHOLD is defined as 1/1000 of a second as this is accurate enough to
 * perform operations with playhead positions, for example, to determine that a playhead
 * position is different from another playhead position.
 *
 * A difference between two different playhead positions (both of them given in seconds) is done
 * by comparing the difference between them, and if the absolute value of that difference is less
 * than 1/1000 second then they are considered to be equal otherwise they are considered to be
 * different.
 *
 * Examples:
 * abs(7.584156 - 7.584159) = 0.000003 is less than 0.001 so these are considered equal
 * abs(7.588 - 7.590) = 0.002 is greater than 0.001 so these are considered different
 */
export const TIME_THRESHOLD = 0.001;

export function isRequestContextMatch(eventContext: any, expectedContext: string): boolean {
  if (!eventContext || !expectedContext) {
    return true;
  }

  if (typeof eventContext === 'string') {
    return eventContext === expectedContext;
  }

  if (typeof eventContext === 'object') {
    const contextName =
      eventContext.name ??
      eventContext.contextName ??
      eventContext.context ??
      eventContext.type ??
      eventContext.requestContext;

    return contextName === expectedContext;
  }

  return false;
}

export function createCsaiAdInfo(
  adEvent: any,
  adsManager: any,
  activeAdBreakInfo?: AdBreakInfo,
  delivery?: AdDeliveryProperties,
): AdInfo {
  const adData = adEvent ? adEvent.getAdData() : undefined;
  const adPodInfo = adData.adPodInfo;
  return {
    adID: adData.adId || ' ',
    sequence: adPodInfo.adPosition,
    adStartTimeOffsetMs: adPodInfo.timeOffset * 1000,
    durationMs: adData.duration * 1000,
    remainingTimeMs: adsManager.getRemainingTime() * 1000,
    isSkippable: adData.skippable,
    skipOffsetMs: adData.skipTimeOffset * 1000,
    isFiller: false,
    mediaUrl: adData.mediaUrl,
    adBreakInfo: activeAdBreakInfo || undefined,
    vastProperties: adData ? getCsaiVastProperties(adData) : undefined,
    delivery,
    adSessionId: createAdSessionId(),
  };
}

export function createCsaiAdBreakInfo(adEvent: any, adsManager: any): AdBreakInfo {
  const adData = adEvent ? adEvent.getAdData() : undefined;
  const adPodInfo = adData.adPodInfo;
  const isLastAd = adPodInfo.totalAds === adPodInfo.adPosition;
  return {
    adBreakID: ' ',
    contentTimePosition: getContentTimePosition(adPodInfo.podIndex),
    adSequencePosition: 1,
    adBreakStartTimeOffsetMs: adPodInfo.timeOffset * 1000,
    remainingTimeMs: adsManager.getRemainingTime() * 1000,
    durationMs: isLastAd ? adData.duration * 1000 : adPodInfo.maxDuration * 1000,
    totalAds: adPodInfo.totalAds,
    adBreakIndex: adPodInfo.podIndex,
  };
}

export function createSsaiAdInfo(
  adEvent: any,
  adBreakInfo: AdBreakInfo | undefined,
  delivery?: AdDeliveryProperties,
): AdInfo {
  const adData = adEvent.getAd();
  const adPodInfo = adData.getAdPodInfo();
  return {
    adBreakInfo: adBreakInfo,
    adID: adData.getAdId(),
    adStartTimeOffsetMs: adPodInfo.getTimeOffset() * 1000,
    durationMs: adData.getDuration() * 1000,
    isFiller: false,
    isSkippable: adData.isSkippable(),
    mediaUrl: '',
    remainingTimeMs: -1,
    sequence: adPodInfo.getAdPosition(),
    skipOffsetMs: adData.getSkipTimeOffset() >= 0 ? adData.getSkipTimeOffset() * 1000 : -1,
    vastProperties: adData ? getSsaiVastProperties(adData) : undefined,
    delivery,
    adSessionId: createAdSessionId(),
  };
}

export function toAd(adInfo: AdInfo): Ad {
  return {
    adBreak: undefined,
    adId: adInfo.adID,
    contentDurationMs: adInfo.durationMs,
    contentPlayheadPositionMs: 0,
    duration: adInfo.durationMs || 0,
    isAutoPlay: false,
    isSkippable: adInfo.isSkippable,
    sequence: adInfo.sequence,
    skipOffset: adInfo.skipOffsetMs,
    start: adInfo.adStartTimeOffsetMs || 0,
    title: '',
    vastProperties: adInfo.vastProperties,
  };
}

export function createAdBreakInfo(adPodInfo: any): AdBreakInfo {
  return {
    adBreakID: '',
    adBreakIndex: adPodInfo.getPodIndex(),
    adBreakStartTimeOffsetMs: adPodInfo.getTimeOffset() >= 0 ? adPodInfo.getTimeOffset() * 1000 : -1,
    adSequencePosition: -1,
    contentTimePosition:
      adPodInfo.getPodIndex() === 0
        ? AdContentTimePosition.PREROLL
        : adPodInfo.getPodIndex() === -1
          ? AdContentTimePosition.POSTROLL
          : AdContentTimePosition.MIDROLL,
    durationMs: adPodInfo.getMaxDuration() * 1000,
    remainingTimeMs: -1,
    totalAds: adPodInfo.getTotalAds(),
  };
}

export function toAdBreak(adBreakInfo: AdBreakInfo): AdBreak {
  return {
    active: true,
    duration: adBreakInfo.durationMs / 1000,
    isSkippable: true,
    position: adBreakInfo.contentTimePosition,
    startTime: adBreakInfo.adBreakStartTimeOffsetMs / 1000,
    totalAds: adBreakInfo.totalAds,
  };
}

export function createSsaiAdBreaks(adCues: any, streamDuration: number): AdBreak[] {
  const cuepoints = adCues && adCues.length > 0 ? adCues : [];
  return cuepoints.map((cuepoint: any) => {
    const adBreakDuration = parseFloat((cuepoint.end - cuepoint.start).toFixed(3));
    return {
      position:
        cuepoint.start === 0
          ? AdContentTimePosition.PREROLL
          : streamDuration - adBreakDuration === cuepoint.start
            ? AdContentTimePosition.POSTROLL
            : AdContentTimePosition.MIDROLL,
      startTime: cuepoint.start,
      duration: adBreakDuration,
      totalAds: -1,
      active: true,
    };
  });
}

export function setImaSdkSettings(settings: any, imaSdkSettings: ImaSdkSettings): void {
  if (imaSdkSettings.disableCustomPlaybackForIOS10Plus !== undefined) {
    settings.setDisableCustomPlaybackForIOS10Plus(imaSdkSettings.disableCustomPlaybackForIOS10Plus);
  }
  if (imaSdkSettings.cookiesEnabled !== undefined) {
    settings.setCookiesEnabled(imaSdkSettings.cookiesEnabled);
  }
  if (imaSdkSettings.autoPlayAdBreaks !== undefined) {
    settings.setAutoPlayAdBreaks(imaSdkSettings.autoPlayAdBreaks);
  }
  if (imaSdkSettings.featureFlags !== undefined) {
    settings.setFeatureFlags(imaSdkSettings.featureFlags);
  }
  if (imaSdkSettings.ppid !== undefined) {
    settings.setPpid(imaSdkSettings.ppid);
  }
  if (imaSdkSettings.playerVersion !== undefined) {
    settings.setPlayerVersion(imaSdkSettings.playerVersion);
  }
  if (imaSdkSettings.companionBackfillMode !== undefined) {
    settings.setCompanionBackfill(imaSdkSettings.companionBackfillMode);
  }
  if (imaSdkSettings.locale !== undefined) {
    settings.setLocale(imaSdkSettings.locale);
  }
  if (imaSdkSettings.numRedirects !== undefined) {
    settings.setNumRedirects(imaSdkSettings.numRedirects);
  }
  if (imaSdkSettings.playerType !== undefined) {
    settings.setPlayerType(imaSdkSettings.playerType);
  }
  if (imaSdkSettings.vpaidMode !== undefined) {
    settings.setVpaidMode(imaSdkSettings.vpaidMode);
  }
  if (imaSdkSettings.sessionId !== undefined) {
    settings.setSessionId(imaSdkSettings.sessionId);
  }
}

export function setAdsRenderingSettings(
  adsRenderingSettings: any,
  renderingInfo: AdsRenderingSettings | undefined,
  imaSDK: any,
  playAdsAfterTime?: number,
): any {
  if (!renderingInfo) {
    return adsRenderingSettings;
  }

  adsRenderingSettings.autoAlign = renderingInfo.autoAlign ?? false;
  adsRenderingSettings.bitrate = renderingInfo.bitrate ?? -1;
  adsRenderingSettings.enablePreloading = renderingInfo.enablePreloading ?? false;
  adsRenderingSettings.loadVideoTimeout = renderingInfo.loadVideoTimeout ?? -1;
  adsRenderingSettings.mimeTypes = renderingInfo.mimeTypes ?? [];
  adsRenderingSettings.UiElements = renderingInfo.enableUiElements
    ? [imaSDK.UiElements.AD_ATTRIBUTION, imaSDK.UiElements.COUNTDOWN]
    : [];
  adsRenderingSettings.useStyledLinearAds = renderingInfo.useStyledLinearAds ?? false;
  adsRenderingSettings.useStyledNonLinearAds = renderingInfo.useStyledNonLinearAds ?? false;

  if (renderingInfo.playAdsAfterTime !== undefined) {
    adsRenderingSettings.playAdsAfterTime = renderingInfo.playAdsAfterTime;
  } else if (playAdsAfterTime !== undefined) {
    adsRenderingSettings.playAdsAfterTime = playAdsAfterTime;
  }

  return adsRenderingSettings;
}

export function formatStreamTimelineMetadata(metadata?: StreamTimelineMetadata): string {
  if (metadata === undefined) {
    return 'undefined';
  }

  return typeof metadata === 'string' ? metadata : JSON.stringify(metadata);
}

function assignIfDefined<T extends object, K extends keyof T>(target: T, key: K, value: T[K] | undefined): void {
  if (value !== undefined) {
    target[key] = value;
  }
}

export function createAdsRequest(imaSDK: typeof google.ima, request: IMAAdRequest): any {
  const adsRequest = new imaSDK.AdsRequest();

  assignIfDefined(adsRequest, 'adTagUrl', request.adTagUrl);
  assignIfDefined(adsRequest, 'linearAdSlotWidth', request.slotWidth);
  assignIfDefined(adsRequest, 'linearAdSlotHeight', request.slotHeight);
  assignIfDefined(adsRequest, 'nonLinearAdSlotWidth', request.nonLinearAdSlotWidth);
  assignIfDefined(adsRequest, 'nonLinearAdSlotHeight', request.nonLinearAdSlotHeight);
  assignIfDefined(adsRequest, 'adsResponse', request.adsResponse);
  assignIfDefined(adsRequest, 'contentDuration', request.contentDuration);
  assignIfDefined(adsRequest, 'contentTitle', request.contentTitle);
  assignIfDefined(adsRequest, 'forceNonLinearFullSlot', request.forceNonLinearFullSlot);
  assignIfDefined(adsRequest, 'liveStreamPrefetchSeconds', request.liveStreamPrefetchSeconds);
  assignIfDefined(adsRequest, 'omidAccessModeRules', request.omidAccessModeRules);
  assignIfDefined(adsRequest, 'pageUrl', request.pageUrl);
  assignIfDefined(adsRequest, 'vastLoadTimeout', request.vastLoadTimeout);

  if (request.contentKeywords?.length) {
    adsRequest.contentKeywords = request.contentKeywords;
  }

  if (request.isAdsAutoPlay !== undefined) {
    adsRequest.setAdWillAutoPlay?.(request.isAdsAutoPlay);
  }

  if (request.isAdsPlayInMute !== undefined) {
    adsRequest.setAdWillPlayMuted?.(request.isAdsPlayInMute);
  }

  if (request.isContentPlayContinuous !== undefined) {
    adsRequest.setContinuousPlayback?.(request.isContentPlayContinuous);
  }

  return adsRequest;
}

function getContentTimePosition(podIndex?: number): AdContentTimePosition {
  if (podIndex === undefined) {
    return AdContentTimePosition.UNKNOWN;
  }
  switch (podIndex) {
    case 0:
      return AdContentTimePosition.PREROLL;
    case -1:
      return AdContentTimePosition.POSTROLL;
    default:
      return AdContentTimePosition.MIDROLL;
  }
}

function getCsaiVastProperties(adData: any): VASTProperties {
  return {
    adSystem: adData.adSystem,
    adTitle: adData.title,
    description: adData.description,
    advertiser: adData.advertiserName,
    survey: adData.surveyUrl,
    creativeId: adData.creativeId,
    bitrate: adData.vastMediaBitrate,
    apiFramework: adData.apiFramework ?? undefined,
    wrapperAdIds: adData.adWrapperIds,
    wrapperAdSystems: adData.adWrapperSystems,
    wrapperCreativeIds: adData.adWrapperCreativeIds,
  };
}

function getSsaiVastProperties(adData: any): VASTProperties {
  return {
    adSystem: adData.getAdSystem(),
    adTitle: adData.getTitle(),
    description: adData.getDescription(),
    advertiser: adData.getAdvertiserName(),
    creativeId: adData.getCreativeId(),
    wrapperAdIds: adData.getWrapperAdIds?.(),
    wrapperAdSystems: adData.getWrapperAdSystems?.(),
    wrapperCreativeIds: adData.getWrapperCreativeIds?.(),
  };
}

/**
 * Returns true if the provided IMAAdRequest request is an instance of SSAIAdRequest. Otherwise
 * returns false.
 * @param request
 */
export function instanceOfSSAIAdsRequest(request: IMAAdRequest): request is SsaiAdRequest {
  return request !== null && typeof request === 'object' && 'format' in request;
}

/**
 * Returns true if the provided SSAIAdsRequest request is an instance of LiveSSAIAdsRequest.
 * Otherwise returns false.
 * @param request
 */
export function instanceOfLiveSSAIAdsRequest(request: SsaiAdRequest): request is LiveSsaiAdsRequest {
  return request !== null && typeof request === 'object' && 'assetKey' in request;
}

/**
 * Returns true if the provided SSAIAdsRequest request is an instance of VodSSAIAdsRequest.
 * Otherwise returns false.
 * @param request
 */
export function instanceOfVodSSAIAdsRequest(request: SsaiAdRequest): request is VodSsaiAdsRequest {
  return request !== null && typeof request === 'object' && 'contentSourceId' in request;
}

/**
 * Returns the total duration of all provided ad breaks.
 * @param adBreaks the list of ad breaks
 */
export function getTotalAdvertsBreaksDuration(adBreaks: AdBreak[]): number {
  return adBreaks
    .map((adBreak: AdBreak) => adBreak.duration)
    .reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);
}

/**
 * Returns the total duration of all provided ad breaks of which start time
 * is before the current time position.
 * @param currentTime the current time position.
 * @param adBreaks the list of ad breaks
 */
export function getAdvertBreaksDurationPriorTo(currentTime: number, adBreaks: AdBreak[]): number {
  return adBreaks
    .map((adBreak: AdBreak) => (adBreak.startTime < currentTime ? adBreak.duration : 0))
    .reduce((accumulator: number, currentValue: number) => accumulator + currentValue);
}

export function getAdBreakInPlayhead(playhead: number, adBreaks: AdBreak[] | undefined): AdBreak | undefined {
  if (!adBreaks || !adBreaks.length) {
    return undefined;
  }

  for (const adBreak of adBreaks) {
    if (compare(adBreak.startTime, playhead) && playhead < adBreak.startTime + adBreak.duration) {
      return adBreak;
    }
  }

  return undefined;
}

/**
 * Given two timestamps this function will return a boolean value indicating whether the first
 * timestamp is less than or equal to the second timestamp or not.
 *
 * Two timestamps are considered equal when the absolute value of the difference between the two
 * timestamps is less than TIME_THRESHOLD.
 *
 * @param first the first timestamp
 * @param second the second timestamp
 */
function compare(first: number, second: number): boolean {
  if (first < second) {
    return true;
  }

  return Math.abs(first - second) < TIME_THRESHOLD;
}

/**
 * Returns a list of all active ad breaks of which start time is between the given range.
 * @param startPosition the start position.
 * @param endPosition the end position.
 * @param adBreaks the list of ad breaks
 */
export function getAdBreaksBetween(startPosition: number, endPosition: number, adBreaks: AdBreak[]): AdBreak[] {
  return adBreaks.filter(
    (adBreak: AdBreak) =>
      adBreak.active && adBreak.startTime >= startPosition && adBreak.startTime <= endPosition && adBreak.duration > 0,
  );
}

/**
 * Returns the total duration of the completed ad breaks.
 * @param playhead the playhead position
 * @param adBreak the list of ad breaks
 * @param ads the list of ads
 */
export function currentAdBreakCompletedAdsDuration(playhead: number, adBreak: AdBreak, ads: Ad[] | undefined): number {
  if (!ads) {
    return 0;
  }

  const filteredAds: Ad[] = ads.filter((ad: Ad) => ad.adBreak?.startTime === adBreak.startTime);

  let adStart = adBreak.startTime;

  let completedAdsDuration = 0;

  for (let i = 0; i < filteredAds.length; i++) {
    // if adStart less than or equal to playhead
    if (compare(adStart, playhead) && playhead < adStart + filteredAds[i].duration) {
      return completedAdsDuration;
    }

    adStart += filteredAds[i].duration;
    completedAdsDuration += filteredAds[i].duration;
  }

  return completedAdsDuration;
}

/**
 * Returns the current Ad duration if any.
 * @param playhead the playhead position
 * @param adBreak the ad break
 * @param adBreaks the list of ad breaks
 * @param ads the list of ads
 */
export function currentAdDuration(
  playhead: number,
  adBreak: AdBreak,
  adBreaks: AdBreak[] | undefined,
  ads: Ad[] | undefined,
): number | null {
  if (!adBreaks) {
    return null;
  }

  const filteredAds: Ad[] = ads ? ads.filter((ad: Ad) => ad.adBreak?.startTime === adBreak.startTime) : [];

  let adStart = adBreak.startTime;

  for (let i = 0; i < filteredAds.length; i++) {
    if (compare(adStart, playhead) && playhead < adStart + filteredAds[i].duration) {
      return filteredAds[i].duration;
    }

    adStart += filteredAds[i].duration;
  }

  return null;
}

/**
 * Returns the ad end time corresponding to the current ad.
 * @param currentAd the current ad
 * @param ads the list of ads
 */
export function getCurrentAdEndTime(currentAd: Ad, ads: Ad[] | undefined): number {
  const completedCurrentAdBreakAdsDuration = ads
    ?.filter((ad: Ad) => ad.adBreak?.startTime === currentAd.adBreak?.startTime && ad.sequence < currentAd.sequence)
    .map((filteredAd: Ad) => filteredAd.duration)
    .reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);

  if (completedCurrentAdBreakAdsDuration && currentAd.adBreak) {
    return currentAd.adBreak.startTime + completedCurrentAdBreakAdsDuration + currentAd.duration;
  }

  return currentAd.duration;
}

export function createAdPlaybackFailure(event: any): Error {
  return createError(
    ImaSsaiErrorCodes.AD_PLAYBACK_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
    "Ad Playback failed. Please check top level error's internal error code for underlying failure reason.",
    event,
  );
}

export function updateAdBreaks(
  adBreaks: AdBreak[] | undefined,
  adBreakInfo: AdBreakInfo | undefined,
): AdBreak[] | null {
  if (!adBreaks || !adBreakInfo) {
    return null;
  }

  let isUpdated = false;

  adBreaks.forEach((adBreak: AdBreak, index: number) => {
    if (adBreak.startTime === adBreakInfo.adBreakStartTimeOffsetMs / 1000) {
      adBreaks[index] = { ...adBreak, ...toAdBreak(adBreakInfo) };
      isUpdated = true;
    }
  });

  if (isUpdated) {
    return adBreaks;
  }

  return null;
}

export function updateAdBreakAfterInterruption(
  activeAdInfo: AdInfo,
  activeAdBreakInfo: AdBreakInfo,
  completedAdsDuration: number,
) {
  completedAdsDuration += activeAdInfo.durationMs || 0;

  activeAdBreakInfo.remainingTimeMs = Math.max(0, activeAdBreakInfo.durationMs - completedAdsDuration);

  activeAdInfo.remainingTimeMs = 0;

  return completedAdsDuration;
}
